/**
 * PushNotificationSettings — small opt-in card users can drop anywhere
 * (currently rendered above the notifications inbox).  Handles the whole
 * permission dance and shows the right CTA based on the current state.
 */
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, BellOff, Send, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { AuthContext } from '../App';
import {
  isPushSupported,
  currentPermission,
  getExistingSubscription,
  enableWebPush,
  disableWebPush,
  sendTestPush,
} from '../lib/webpush';

const PushNotificationSettings = () => {
  const { API } = useContext(AuthContext);
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSupported(isPushSupported());
    setPermission(currentPermission());
    (async () => {
      const sub = await getExistingSubscription();
      setSubscribed(!!sub);
    })();
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    const r = await enableWebPush(API);
    setBusy(false);
    setPermission(currentPermission());
    if (r.ok) {
      setSubscribed(true);
      toast.success('Push notifications enabled on this device');
    } else if (r.reason === 'permission_denied') {
      toast.error('Permission denied. Open your browser site settings to re-allow notifications.');
    } else if (r.reason === 'unsupported') {
      toast.error('Your browser does not support push notifications');
    } else {
      toast.error('Could not enable push. Try again in a moment.');
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    const r = await disableWebPush(API);
    setBusy(false);
    if (r.ok) {
      setSubscribed(false);
      toast.success('Push notifications disabled on this device');
    }
  };

  const handleTest = async () => {
    setBusy(true);
    const r = await sendTestPush(API);
    setBusy(false);
    if (r.ok) toast.success(`Test push sent to ${r.delivered} device${r.delivered === 1 ? '' : 's'}`);
    else toast.error('Test push failed');
  };

  if (!supported) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4" data-testid="push-unsupported">
        <div className="h-11 w-11 rounded-xl bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-heading text-sm font-extrabold tracking-tight">Push not supported here</div>
          <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            This browser doesn&apos;t support web-push. Try Chrome, Edge, Firefox — or install the app to your home screen on iOS 16.4+.
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5 flex items-start gap-4" data-testid="push-denied">
        <div className="h-11 w-11 rounded-xl bg-destructive/15 text-destructive flex items-center justify-center flex-shrink-0">
          <BellOff className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-heading text-sm font-extrabold tracking-tight text-destructive">Push blocked</div>
          <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            You&apos;ve blocked notifications for this site. Click the lock icon in the address bar → Notifications → Allow, then refresh.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="push-settings">
      <div className="flex items-start gap-4">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${subscribed ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
          {subscribed ? <CheckCircle2 className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-extrabold tracking-tight">
            {subscribed ? 'Device notifications are ON' : 'Enable device notifications'}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {subscribed
              ? 'This device will buzz for forum replies, new lessons and admin announcements — even when the tab is closed.'
              : 'Get a push on this device the moment someone replies to your thread, a new lesson drops, or an announcement goes out.'}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!subscribed && (
          <Button
            onClick={handleEnable}
            disabled={busy}
            className="rounded-full font-semibold"
            data-testid="push-enable-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Enable push
          </Button>
        )}
        {subscribed && (
          <>
            <Button
              onClick={handleTest}
              disabled={busy}
              variant="outline"
              className="rounded-full font-semibold"
              data-testid="push-test-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test push
            </Button>
            <Button
              onClick={handleDisable}
              disabled={busy}
              variant="ghost"
              className="rounded-full font-semibold text-muted-foreground"
              data-testid="push-disable-btn"
            >
              <BellOff className="w-4 h-4 mr-2" />
              Turn off on this device
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PushNotificationSettings;
